<script>
  import { cloneDeep } from 'es-toolkit';
  import { computed } from 'vue';

  import useSearch from '../../composables/useSearch/useSearch';
  import { CSS_VARS, LLLV_CHANGE_TRIGGERS } from '../../constants';
  import sticky from '../../directives/sticky/sticky';
  import { t } from '../../locale';
  import { persistentStorage } from '../../storage';
  import { filterItems as _filterItems, getCssVar, sortItems as _sortItems } from '../../utils/helpers';
  import Badge from '../Badge/Badge.vue';
  import Button from '../Button/Button.vue';
  import Checkbox from '../Checkbox/Checkbox.vue';
  import Dropdown from '../Dropdown/Dropdown.vue';
  import Expand from '../Expand/Expand.vue';
  import Filters from '../Filters/Filters.vue';
  import Icon from '../Icon/Icon.vue';
  import Input from '../Input/Input.vue';
  import Paginate from '../Paginate/Paginate.vue';
  import BulkActions from './components/BulkActions.vue';
  import LoadingManager from './components/LoadingManager.vue';

  export { default as BulkActions } from './components/BulkActions.vue';
  export { default as ListGroup } from './components/ListGroup.vue';
  export { default as ListHeader } from './components/ListHeader.vue';
  export { default as LoadingManager } from './components/LoadingManager.vue';

  const DEFAULT_SEARCH_SCHEMA = {
    isSearchable: true,
    placeholder: 'Search',
    searchBy: ['name'],
  };

  export default {
    name: 'll-list-view',

    components: {
      BulkActions,
      Filters,
      Paginate,
      LoadingManager,
      Badge,
      Button,
      Icon,
      Dropdown,
      Expand,
      Checkbox,
      Input,
    },

    directives: {
      sticky,
    },

    inject: {
      trackSearch: { default: () => () => {} },
      trackFilters: { default: () => () => {} },
      trackSort: { default: () => () => {} },
      trackResults: { default: () => () => {} },
    },

    props: {
      /**
       * Hide the '{number} of {total} selected' text for bulk actions
       */
      hideNumberOfTotalSelected: {
        type: Boolean,
        default: false,
      },
      /**
       * Hide the Select All checkbox for bulk actions
       */
      hideSelectAll: {
        type: Boolean,
        default: false,
      },
      /**
       * Text to render in EmptyState component if there are no results
       */
      emptyStateText: {
        type: String,
        default: '',
      },
      filterSchema: {
        type: Array,
        default: () => [],
      },
      /**
       * Make search fuzzy. Only for client side search.
       */
      fuzzySearch: {
        type: Boolean,
        default: false,
      },
      hideTotal: Boolean,
      initialPage: {
        type: Number,
        default: undefined,
      },
      /**
       * Can items on the page be selected for bulk actions
       */
      isSelectable: Boolean,
      /**
       * For loading data already paginated, filtered (including search), and sorted from a backend.
       * Defining this prop will disable the built-in search, filtering, sorting and pagination.
       * You will instead need to use the available events (`@change`, and `@change:` prefixed)
       * to know when to request new pages from your backend.
       * This requires providing `totalItemCount`.
       * Use the loading prop to indicate when data is being loaded.
       */
      isServerSide: Boolean,
      /**
       * The data for each row. Used in this file as this.internalItems (a copy) to allow mutation;
       */
      items: {
        type: Array,
        default: () => [],
      },
      /**
       * Default field to track selected items by
       */
      itemTrackBy: {
        type: String,
        default: 'id',
      },
      loading: Boolean,
      // Number of items to display per page
      pageSize: {
        type: Number,
        default: 50,
      },
      searchSchema: {
        type: Object,
        default: () => ({}),
      },
      // Provide an optional custom 'Sort by' label
      sortLabel: {
        type: String,
        default: () => t('ll.listView.sortBy'),
      },
      sortOnLoad: Boolean,
      sorts: {
        type: Object,
        default: () => ({}),
      },
      /**
       * Total number of unpaginated results
       * Required when `isServerSide` is `true`
       */
      totalItemCount: {
        type: Number,
        default: undefined,
      },
      /**
       * Validation schema function that returns an object
       */
      validationSchema: {
        type: Function,
        default: () => () => undefined,
      },
      /**
       * Whether to disable the sticky filters
       * if disabled, the following will happen
       * 1. pre-selected value(s) can be added to
       * the filter component's attributes to overwrite the sticky filters
       * 2. users will not have sticky filters on the page
       */
      disablePersistency: Boolean,
      /**
       * Whether to disable the ability of saving 'show-filters'
       * in local storage
       */
      disableShowFilter: Boolean,
      /**
       * Whether to disable the stickiness of the header.
       * Default is false. When true, neither the list headers nor
       * the bulk actions (when applicable) will be sticky.
       */
      disableStickyHeader: Boolean,
      /**
       * Whether to expand the filters section by default on load.
       */
      showFiltersOnLoad: Boolean,

      /**
       * Do not use the LoadingManager, and instead render the default slot as passed
       */
      disableLoader: {
        type: Boolean,
        default: false,
      },

      /**
       * Override the header offset used to position the sticky header
       */
      stickyHeaderOffset: {
        type: Number,
        default: null,
      },
      /**
       * Prevent bulk actions dropdown from rendering
       */
      hideBulkActionOptions: {
        type: Boolean,
        default: false,
      },

      /**
       * If initial search term is provided, the list will be filtered on load.
       */
      initialSearchTerm: {
        type: String,
        default: '',
      },

      /**
       * Function that determines whether the item checkbox is disabled or not (default: false)
       */
      isItemDisabled: {
        type: Function,
        default: () => false,
      },
    },

    emits: [
      'change',
      'change:filter',
      'change:page',
      'change:search',
      'change:sort',
      'change:reset',
      'filters-toggle-click',
      'select-all',
      'deselect-all',
      'update-filters',
      'updateResults',
    ],

    data() {
      return {
        internalItems: [],
        currentPage: this.initialPage || 1, // TODO: fetch from query params
        previousPage: null,
        sortOptionsWidth: 0,
        filters: {},
        filterText: t('ll.filter'),
        searchFor: () => {}, // initialized in the watch handler for internalItems
        searchTerm: this.initialSearchTerm,
        searchText: t('ll.search'),
        sortTerm: '',
        isLoadingSearchSchemaAction: false,
        isShowingFilters: !!(
          this.filterSchema?.length &&
          (this.showFiltersOnLoad || persistentStorage.get('show-filters'))
        ),
        isFiltering: false,
        results: [],
        selectedItems: [],
      };
    },

    computed: {
      /**
       * Returns true if any of its list items are truthy; otherwise returns false.
       * @returns {boolean}
       */
      isLoading() {
        return [this.loading, this.isLoadingSearchSchemaAction, this.isFiltering].some(Boolean);
      },

      totalCount() {
        return this.isServerSide ? this.totalItemCount : this.results.length;
      },

      /**
       * Helps determine if the list is filtered or not when there is an incomplete
       * result list, such as when isServerSide is true.
       */
      isFiltered() {
        return !!(this.numberOfFilters || this.searchTerm);
      },

      /**
       * Merges the searchSchema with the default value since all attributes are optional
       */
      internalSearchSchema() {
        return {
          ...DEFAULT_SEARCH_SCHEMA,
          searchBy: !this.searchSchema.isSearchable && DEFAULT_SEARCH_SCHEMA.searchBy,
          ...this.searchSchema,
        };
      },

      /**
       * Start of page
       */
      startIndex() {
        return this.pageSize * (this.currentPage - 1);
      },

      /**
       * End of page
       */
      endIndex() {
        return this.startIndex + this.pageSize;
      },

      /**
       * Slices the results set into a paginated segment.
       * @returns One page in the paginated set of results
       */
      paginated() {
        // Disables the built-in client-side pagination
        let paginated;

        if (this.isServerSide) {
          paginated = this.results;
        } else {
          const { endIndex, totalCount, results, startIndex } = this;

          paginated = results.slice(startIndex, Math.min(endIndex, totalCount));
        }

        this.$emit('updateResults', paginated);

        return paginated;
      },

      /**
       * Displays a visual indication for the number of results, or "working" if the
       * results are being determined.
       */
      total() {
        const current = this.pageSize * (this.currentPage - 1);
        const range = this.paginated.length ? `${current + 1} - ${current + this.paginated.length}` : 0;

        return this.isLoading
          ? t('ll.listView.calculating')
          : t('ll.listView.totalResults', { num: String(range), total: String(this.totalCount) });
      },

      /**
       * Determines if both #list-header and #list-items slots are used.
       * @returns {boolean}
       */
      useDefinedSlots() {
        return !!this.$slots['list-header'] && !!this.$slots['list-items'];
      },

      /**
       * Don't show total count if list items are selected and total is not hidden
       */
      shouldShowTotal() {
        return !this.totalSelectedCount && !this.hideTotal;
      },

      /**
       * Returns a list of the selected items' track by field
       */
      selectedItemIds() {
        return this.selectedItems.map((item) => item[this.itemTrackBy]);
      },

      /**
       * Returns a list of the paginated items' track by field
       */
      paginatedIds() {
        return this.paginated.map((item) => item[this.itemTrackBy]);
      },

      isAllPageChecked() {
        if (this.isServerSide) {
          return this.totalCount > 0 && this.totalCount === this.selectedItemIds.length;
        }

        return this.totalCount > 0 && this.paginatedIds.every((id) => this.selectedItemIds.includes(id));
      },

      isSomePageChecked() {
        if (this.isServerSide) {
          return this.totalCount > 0 && this.selectedItemIds.length > 0;
        }

        return this.totalCount > 0 && this.paginatedIds.some((id) => this.selectedItemIds.includes(id));
      },

      numberOfFilters() {
        return Object.keys(this.filters).reduce((acc, item) => {
          const filterBy = this.filters[item].filterBy;
          const isBoolean = typeof filterBy[0] === 'boolean';
          const isToggle = this.filters[item].filterType === 'll-checkbox';
          const isEmpty = !this.filters[item].filterType.length;
          const isInputOptionsIncomplete = this.filters[item].filterType === 'll-input-options' && filterBy[0] === '';

          /**
           * Don't include hidden or incomplete fields in the count:
           * - Hidden filter populated via another filter's onChange callback
           * - InputOptions with incomplete/missing values
           */
          if (isEmpty || isInputOptionsIncomplete) {
            return acc;
          }

          if (isBoolean || typeof filterBy[0] === 'string') {
            // Do not count toggle filters (eg. checkbox) with a `false` value
            return acc + (filterBy[0] || !isToggle ? 1 : 0);
          }

          return acc + (filterBy.length || 0);
        }, 0);
      },

      isSelectBarVisible() {
        return !!this.totalSelectedCount;
      },

      showPrimaryControls() {
        return this.internalSearchSchema.isSearchable || this.filterSchema.length;
      },

      showSecondaryControls() {
        return !!this.$slots['secondary-controls'];
      },

      isSortable() {
        return Object.keys(this.sorts).length > 0;
      },

      totalSelectedCount() {
        return this.selectedItems.length;
      },

      headerOffset() {
        // passing null to the v-sticky directive will prevent it from being attached to the node
        if (this.disableStickyHeader) {
          return null;
        }

        return this.stickyHeaderOffset ?? parseFloat(getCssVar(CSS_VARS.TOP_HEADER_HEIGHT));
      },
    },

    watch: {
      isShowingFilters(value) {
        if (!this.disableShowFilter) {
          persistentStorage.set('show-filters', value);
        }
      },

      items: {
        handler() {
          this.internalItems = cloneDeep(this.items);
        },
        immediate: true,
        deep: true,
      },

      internalItems: {
        handler() {
          if (this.isServerSide) {
            this.results = this.internalItems;

            this.trackResults({
              page: this.currentPage,
              results: this.results,
              search: this.searchTerm,
            });

            return;
          }

          const { searchFor } = useSearch({
            items: computed(() => this.internalItems),
            fieldNames: this.internalSearchSchema.searchBy.length
              ? this.internalSearchSchema.searchBy
              : [this.displayBy],
            trackBy: this.itemTrackBy,
          });

          this.searchFor = (searchTerm) => searchFor(searchTerm, { fuzzy: this.fuzzySearch });

          this.processResults();
        },
        immediate: true,
      },

      sortTerm() {
        if (this.isServerSide) {
          return;
        }

        this.processResults();
      },

      sorts: {
        handler(value) {
          const sortTerms = Object.keys(value);

          this.sortTerm = this.sortOnLoad && sortTerms.length ? sortTerms[0] : '';
        },
        immediate: true,
      },
    },

    created() {
      if (this.isServerSide) {
        if (this.totalItemCount === undefined) {
          throw new Error('`totalItemCount` is required when `isServerSide` is `true`.');
        }

        return;
      }

      if (this.searchSchema.action && this.internalItems.length) {
        throw new Error('You must pass either `action` or `items`, not both.');
      }

      if (this.searchTerm) {
        this.onSearch();
      }
    },

    methods: {
      /**
       * Processes the results with searchFor, filterItems, and/or sortItems.
       * Skipped when isServerSide is true because results are pre-processed.
       * @returns {Promise<void>}
       */
      async processResults() {
        if (this.searchSchema.action) {
          this.isLoadingSearchSchemaAction = true;
          let actionResponse;

          try {
            actionResponse = await this.searchSchema.action({ search: this.searchTerm });
          } finally {
            this.isLoadingSearchSchemaAction = false;
          }

          this.results = this.sortItems(this.filterItems(actionResponse?.results || []));
        } else {
          this.results = this.sortItems(this.filterItems(this.searchFor(this.searchTerm)));
        }

        this.trackResults({
          page: this.currentPage,
          results: this.results,
          search: this.searchTerm,
        });
      },

      /**
       * Filters the list of `items` based on options selected in the `filters` object
       * @param {array} items A list of items to filter.
       * @returns list of items filtered by the applied filters
       */
      filterItems(items) {
        return _filterItems(items, this.filters);
      },

      /**
       * Sorts a list of items based on options selected in the `sorts` object
       * @param {array} items A list of items to sort.
       * @returns list of items sorted according to the sort term.
       */
      sortItems(items) {
        return this.sortTerm ? _sortItems(items, this.sorts[this.sortTerm]) : items;
      },

      change({ trigger = '' } = {}) {
        const { currentPage, filters, isFiltered, pageSize, searchTerm, sortTerm } = this;

        const params = {
          filters,
          isFiltered,
          page: {
            number: currentPage,
            previousNumber: this.previousPage,
            size: pageSize,
          },
          search: searchTerm,
          sort: sortTerm,
          trigger,
        };

        this.$emit('change', params);
      },

      /**
       * Clear search term and reset results to their initial state
       */
      async clearSearchTerm() {
        this.searchTerm = '';
        await this.$attrs.onSearchTermClear?.();
        this.onSearch();
      },

      /**
       * Resets the results to their initial state on page load
       */
      reset() {
        this.$emit('change:reset');

        this.searchTerm = '';

        this.setPage({ pageNumber: 1 });

        if (this.isServerSide) {
          this.change({ trigger: LLLV_CHANGE_TRIGGERS.RESET });

          return;
        }

        this.processResults();
      },

      onSearchTermChange() {
        this.$attrs.onSearchTermChange?.(this.searchTerm);
      },

      /**
       * Event handler for when the user:
       *  - clicks the Search button
       *  - presses the enter key while focused on the search input
       * Calls a method in Filters that triggers `this.onFilter`. This is necessary
       * in order to persist the filters in local storage, which is handled internally
       * by Filters.
       */
      onSearch() {
        this.$refs.llFilters.applyFilters({ trigger: LLLV_CHANGE_TRIGGERS.SEARCH });
      },

      /**
       * Event handler for when the user:
       *  - clicks the Apply button
       *  - clicks the Search button (from `this.onSearch`)
       *  - presses the enter key while focused on the search input (from `this.onSearch`)
       * @see: SELL-350
       * @param {object} [options] - optional parameters
       * @param {string} [options.trigger] - what caused the changes
       * @returns {Promise<void>}
       */
      async onFilter({ trigger = LLLV_CHANGE_TRIGGERS.APPLY } = {}) {
        this.setPage({ pageNumber: 1 });
        this.change({ trigger });
        this.trackSearch(this.searchTerm);
        this.trackFilters(this.filters);
        this.selectedItems = [];

        if (this.isServerSide) {
          return;
        }

        try {
          this.isFiltering = true;
          await this.processResults({ trigger });
        } finally {
          this.isFiltering = false;
        }

        this.$emit('change:search', this.searchTerm);
        this.$emit('change:filter', this.filters);
        this.$emit('update-filters', this.filters); // deprecated; replaced by change:filter
        this.clearSelected();
      },

      onFiltersToggleClick() {
        this.isShowingFilters = !this.isShowingFilters;

        this.$emit('filters-toggle-click', {
          isShowingFilters: this.isShowingFilters,
        });
      },

      /**
       * Extend the dropdown list so it does not shift when
       * the options drawer is open
       */
      onSortToggle() {
        // spacing added to the width of the dropdown container
        const spacing = 36;

        this.$nextTick(() => {
          this.sortOptionsWidth = `${this.$refs.sortOptions.clientWidth + spacing}px`;
        });
      },

      /**
       * Executes any side-effects when the sort `term` is changed
       * Always fires `change` automatically.
       * @param {string} term The updated sort term
       */
      onSort(term) {
        if (term === this.sortTerm) {
          return;
        }

        this.sortTerm = term;

        this.$emit('change:sort', term);
        this.trackSort(term);
        this.clearSelected();
        this.setPage({ pageNumber: 1 });
        this.change({ trigger: LLLV_CHANGE_TRIGGERS.SORT });
      },

      /**
       * Sets current page for the Paginator.
       * @param {Object} data
       * @param {number} data.pageNumber - the page number
       * @param {boolean} data.forceChange - flag to force the change
       */
      setPage({ pageNumber, forceChange = false } = {}) {
        this.previousPage = this.currentPage;

        this.currentPage = pageNumber;

        if (!this.isServerSide) {
          this.$emit('change:page', pageNumber);
        }

        if (forceChange) {
          this.change({ trigger: LLLV_CHANGE_TRIGGERS.PAGE });
        }
      },

      /**
       * Set the scroll position to the top of the list view.
       */
      scrollToTop() {
        if (this.$refs.scrollTarget) {
          this.$refs.scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      },

      onPaginateSetPage(pageNumber) {
        this.setPage({ pageNumber, forceChange: true });
        this.scrollToTop();
      },

      /**
       * Selects or deselects all items on a page
       */
      onSelectPage(value) {
        this.$emit('select-all', value);

        if (value) {
          const items = Array.from(new Set([...this.selectedItems, ...this.paginated]));

          this.selectedItems = this.selectItems(items);
        } else {
          this.selectedItems = this.selectedItems.filter((item) => !this.paginatedIds.includes(item[this.itemTrackBy]));
        }
      },

      selectAll() {
        this.selectedItems = this.selectItems(this.results);
      },

      /**
       * Selects one item
       */
      onSelectItem({ item, value }) {
        if (this.isItemDisabled(item)) {
          return;
        }

        if (value) {
          this.selectedItems.push(item);
        } else {
          this.selectedItems = this.selectedItems.filter(
            (selectedItem) => selectedItem[this.itemTrackBy] !== item[this.itemTrackBy],
          );
        }
      },

      /**
       * Clears current selected items
       */
      clearSelected() {
        this.$emit('deselect-all');
        this.selectedItems = [];
      },

      selectItems(items) {
        return items.reduce((acc, item) => (this.isItemDisabled(item) ? acc : [...acc, item]), []);
      },
    },
  };
</script>

<template>
  <div>
    <div
      v-if="showPrimaryControls || showSecondaryControls"
      ref="scrollTarget"
      class="mb-6"
      :class="{ 'll-grid': showPrimaryControls && showSecondaryControls }"
    >
      <div
        v-if="showPrimaryControls"
        class="controls"
        :class="[$style['legacy-box'], { 'col-span-12 lg:col-span-6': showSecondaryControls }]"
        data-test="filters|controls"
      >
        <div class="ll-grid grid-cols-4 md:grid-cols-8 lg:grid-cols-12">
          <!-- Search -->
          <Input
            v-if="internalSearchSchema.isSearchable"
            ref="search-input"
            v-model="searchTerm"
            class="col-span-3 md:col-span-6 lg:col-span-4"
            data-test="input|search-input"
            :class="[
              $style['search-input'],
              {
                'lg:col-span-8': showSecondaryControls,
                'col-span-4 md:col-span-8 lg:col-span-6': !filterSchema.length,
              },
            ]"
            :hint-text="internalSearchSchema.hintText"
            :label="searchText"
            :placeholder="internalSearchSchema.placeholder"
            @keyup.enter="!isLoading && onSearch()"
            @change="onSearchTermChange"
          >
            <template #append>
              <button
                v-if="searchTerm"
                data-test="button|clear-search"
                :class="$style['clear-button']"
                :disabled="isLoading"
                @click="clearSearchTerm"
              >
                <Icon name="close" />
              </button>
              <Button
                data-test="button|search-button"
                :disabled="isLoading"
                :class="$style['search-button']"
                @click="onSearch"
              >
                <Icon
                  :class="{ 'animate-spin text-teal-500': isFiltering }"
                  :name="isFiltering ? 'working' : 'search'"
                />
              </Button>
            </template>
          </Input>

          <!-- Filter Toggle -->
          <div
            v-if="filterSchema.length"
            class="col-span-1 flex items-end md:col-span-2 lg:col-span-1"
            :class="[
              $style['filter-toggle-btn'],
              { [$style['filter-toggle-btn-with-hint-text']]: !!internalSearchSchema.hintText },
            ]"
          >
            <Badge class="w-full lg:w-auto" :content="numberOfFilters">
              <Button
                secondary
                class="relative w-full min-w-auto"
                color="blue"
                data-test="button|toggle-filters"
                :disabled="isFiltering"
                @click="onFiltersToggleClick"
              >
                <Icon
                  v-if="isFiltering"
                  name="working"
                  :class="[{ 'animate-spin': isFiltering }, isShowingFilters ? 'text-teal-500' : 'text-ice-700']"
                />
                <Icon v-else name="filter-line" />
                {{ filterText }}
              </Button>
            </Badge>
          </div>

          <!-- Actions -->
          <div class="button-grid col-span-1 items-end justify-end md:col-span-6 lg:col-span-3">
            <slot name="actions"> </slot>
          </div>
        </div>

        <!-- Filters -->
        <Expand data-test="filters|content" :class="$style['filters-expand']" :is-expanded="isShowingFilters">
          <div class="relative pt-12" :class="$style['filters-wrapper']">
            <Filters
              ref="llFilters"
              :class="{ [$style['filters-half-width']]: showPrimaryControls && showSecondaryControls }"
              :disable-apply="isLoading"
              :disable-clear="isLoading || !numberOfFilters"
              :disable-persistency="disablePersistency"
              :schema="filterSchema"
              :validation-schema="validationSchema"
              @change="filters = $event"
              @reset="reset"
              @submit="onFilter"
            />
          </div>
        </Expand>
      </div>

      <div
        v-if="showSecondaryControls"
        class="secondary-controls"
        :class="[$style['legacy-box'], { 'col-span-12 mt-0 lg:col-span-6': showPrimaryControls }]"
      >
        <slot name="secondary-controls"></slot>
      </div>
    </div>

    <!-- @slot Renders custom content in the space below the filters and above the list -->
    <slot name="eyebrow"></slot>

    <div
      v-if="shouldShowTotal || Object.keys(sorts).length"
      class="mb-1.5"
      :class="shouldShowTotal ? $style['total-sort'] : 'text-right'"
    >
      <div v-if="shouldShowTotal" class="text-xs">{{ total }}</div>

      <!-- Sort -->
      <Dropdown
        v-if="Object.keys(sorts).length"
        :class="$style.dropdown"
        :label="`${sortLabel} ${sortTerm}`"
        @toggle.once="onSortToggle"
      >
        <ul ref="sortOptions" class="dropdown__list" :style="{ minWidth: sortOptionsWidth }">
          <li
            v-for="(sortOption, index) in Object.keys(sorts)"
            :key="index"
            :class="{ 'bg-blue-100 text-ice-700': sortOption === sortTerm }"
            class="dropdown__item rounded"
          >
            <Button inline class="items-center" :class="$style['sort-option']" @click="onSort(sortOption)">
              {{ sortOption }}
              <Icon v-if="sortOption === sortTerm" name="check" class="ml-auto text-blue-500" />
            </Button>
          </li>
        </ul>
      </Dropdown>
    </div>

    <!-- Defined Slots -->
    <div v-if="useDefinedSlots" :class="$style['list-items']">
      <header
        v-sticky="headerOffset"
        data-test="header|defined-slot"
        :class="[
          $style.header,
          {
            'm-0': !isSelectable,
          },
        ]"
        class="rounded"
      >
        <div :class="$style['header-grid-container']" class="border-b border-ice-200 px-3">
          <Checkbox
            v-if="isSelectable"
            class="ml-3"
            :checked="isSomePageChecked"
            :class="$style['select-all']"
            :indeterminate="isSomePageChecked && !isAllPageChecked"
            @update:checked="onSelectPage"
          />
          <div class="ll-grid flex-1" :class="$style['header-grid']">
            <slot name="list-header"> </slot>
          </div>
        </div>

        <!-- Select Bar -->
        <BulkActions
          v-if="isSelectable"
          v-bind="{
            allowSelectAll: !hideSelectAll && !isServerSide,
            isAllPageChecked,
            isSomePageChecked,
            totalCount,
            totalSelectedCount,
            hideBulkActionOptions,
            hideNumberOfTotalSelected,
          }"
          :class="!isSelectBarVisible && 'lg:hidden'"
          @change:clear-selected="clearSelected"
          @change:select-current="onSelectPage"
          @change:select-all="selectAll"
        >
          <template #bulk-actions-content>
            <slot name="bulk-actions" :selected-items="selectedItems"> </slot>
          </template>
        </BulkActions>
      </header>

      <component
        :is="disableLoader ? 'div' : 'LoadingManager'"
        :empty-state-text="emptyStateText"
        :loading="isLoading"
        :results="paginated"
      >
        <slot
          name="list-items"
          :clear-all="clearSelected"
          :display-items="results"
          :is-filtered="isFiltered"
          :is-item-disabled="isItemDisabled"
          :results="paginated"
          :on-select="onSelectItem"
          :selected-items="selectedItems"
        ></slot>
      </component>
    </div>

    <!-- Default Slot -->
    <slot
      :current-page="currentPage"
      :display-items="results"
      :filters="filters"
      :is-filtered="isFiltered"
      :is-item-disabled="isItemDisabled"
      :results="paginated"
      :search-term="searchTerm"
      :total="totalCount"
    ></slot>

    <!-- Pagination -->
    <Paginate
      v-if="totalCount > pageSize"
      :current-page="currentPage"
      :list-length="totalCount"
      :page-size="pageSize"
      @set-page="onPaginateSetPage"
    />
  </div>
</template>

<style module>
  @reference "../../../styles/main.css";

  .list-items {
    background-color: var(--color-ice-200);
    border-bottom-left-radius: var(--radius-sm);
    border-bottom-right-radius: var(--radius-sm);
    min-width: 100%;
    position: relative;
  }

  @media (width >= theme(--breakpoint-lg)) {
    .list-items {
      @apply shadow;
    }
  }

  /* the .legacy-box styles are copied from .box in _box.scss */
  .legacy-box {
    @apply shadow;

    background: var(--color-white);
    border-radius: var(--radius-sm);
    padding: --spacing(6) var(--grid-gutter);
  }

  .header {
    background-color: var(--color-white);
    color: var(--color-ice-900);
    font-weight: var(--stash-font-weight-medium);
    margin-bottom: --spacing(3);
    overflow: hidden;
    z-index: 1;

    &:global(.is-sticky) {
      @apply shadow;

      border-radius: 0;
      overflow: visible;
      z-index: calc(var(--z-index-control) + 1);

      &::before,
      &::after {
        background-color: var(--color-white);
        content: '';
        height: 100%;
        overflow-x: hidden;
        position: absolute;
        right: 100%;
        top: 0;
        width: --spacing(3);

        @media (width >= theme(--breakpoint-lg)) {
          content: none;
        }
      }

      &::after {
        left: 100%;
        right: auto;
      }
    }

    :global(.input--checkbox) {
      label {
        min-height: --spacing(6);
      }
    }

    @media (width >= theme(--breakpoint-lg)) {
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
      margin-bottom: 0;
    }
  }

  .header-grid-container {
    display: none;
    transition: padding 150 var(--ease-swing);

    @media (width >= theme(--breakpoint-lg)) {
      align-items: center;
      display: flex;
    }
  }

  .header-grid {
    column-gap: 0 !important;
    grid-column-gap: 0 !important;

    > div {
      align-items: center;
      border-right: 1px solid var(--color-ice-200);
      display: flex;
      font-size: var(--text-xs);
      justify-content: space-between;
      padding: --spacing(3);

      &:first-of-type {
        margin-left: calc(-1 * --spacing(3));
        padding-left: --spacing(6);
      }

      &:last-of-type {
        border-right: 0;
        margin-right: calc(-1 * --spacing(3));
      }
    }
  }

  .search-input {
    :global(input) {
      /* to prevent search text from overlapping the "Clear" button */
      padding-right: calc(var(--height-input) * 2) !important;
    }

    :global(.stash-input-append) {
      display: flex;
      justify-content: flex-end;
      width: 72px; /* double the the default width of 36px */

      .clear-button {
        color: var(--color-ice-700);
        height: 36px;
        margin: 0;
        min-width: 36px;
        padding: 0;
        width: 36px;

        &:disabled {
          color: var(--color-ice-500);
          cursor: default;
        }
      }

      .search-button {
        border-bottom-left-radius: 0;
        border-color: var(--color-blue-500);
        border-left: none;
        border-top-left-radius: 0;
        min-width: 36px;
        padding: 0 --spacing(1.5);
        width: 36px;

        &:disabled {
          border-color: var(--color-ice-500);
        }
      }
    }
  }

  @media (width >= theme(--breakpoint-md)) {
    .total-sort {
      align-items: baseline;
      display: flex;
      justify-content: space-between;
    }
  }

  .filter-toggle-btn {
    min-width: fit-content !important; /* ensure this overrides the grid min-width applied */
  }

  .filter-toggle-btn-with-hint-text {
    /* search hint text has 16px of height and has 4px of margin top. Adding here directly to compensate that */
    margin-bottom: 20px;
  }

  @keyframes separator {
    0% {
      opacity: 0;
    }

    100% {
      opacity: 1;
    }
  }

  .filters-expand:global(.stash-expand--expanded) {
    .filters-wrapper::before {
      animation: separator 300 var(--ease-in-out) 0.5s 1 forwards;
      background: var(--color-ice-200);
      content: '';
      height: 1px;
      left: calc(var(--grid-gutter) * -1);
      opacity: 0;
      position: absolute;
      right: calc(var(--grid-gutter) * -1);
      top: --spacing(6);
    }
  }

  .filters-half-width {
    @media (width >= theme(--breakpoint-lg)) {
      :global(.field-wrapper) {
        grid-column: span 6 / span 6; /* col-span-6 */
      }
    }
  }

  .dropdown :global(.text-blue-500) {
    font-size: var(--text-xs);
  }

  .sort-option {
    height: --spacing(9);
  }

  .select-all {
    width: --spacing(6);
  }
</style>
